// Sub-Step 3.3 — OIDC-Config für public/-Stack
//
// Pattern: react-oidc-context AuthProvider mit Silent-SSO via Keycloak.
// Realm "scrolly" auf auth.scrolly.com wird mit dem portal/-Stack
// (Client "scrolly-admin") geteilt — beide haben eigene OIDC-Clients
// und eigenen sessionStorage-Schlüssel, aber gemeinsame Keycloak-
// SSO-Session-Cookie (domain=auth.scrolly.com).
//
// Storage: sessionStorage (oidc-client-ts default, Pre-Verify Q-3.3-3).
// Cross-Tab-Sync fehlt bewusst — Silent-SSO refresht bei Bedarf.
//
// loadUserInfo: false — Pre-Verify Q-3.3-5: kein /auth/me-Call.
// User-ID kommt aus JWT `sub`-Claim, kein extra Backend-Hit.

import type { AuthProviderProps } from "react-oidc-context";
import { stripAuthParams } from "./sso-hint";

// Exportiert damit SsoHandoffReceiver in OidcAuthProvider.tsx den
// identischen storageKey berechnen kann (`oidc.user:${issuer}:${clientId}`).
export const KEYCLOAK_ISSUER =
  import.meta.env.VITE_KEYCLOAK_ISSUER ??
  "https://ppi-kc.scrolly.com/realms/scrolly";

export const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_PUBLIC_CLIENT_ID;

if (!CLIENT_ID) {
  // Hard throw nur wenn ENV-Variable komplett fehlt — sonst kann
  // Build im public/-Stack nicht laufen.
  throw new Error(
    "VITE_KEYCLOAK_PUBLIC_CLIENT_ID is not set. " +
      "Add it to public/.env.development or public/.env.production.",
  );
}

if (CLIENT_ID === "PLACEHOLDER_AWAITING_PPI") {
  // Soft warning — erlaubt das Bauen der Code-Struktur, verhindert
  // nur den echten OIDC-Connect. Browser-Test in S2.8 kommt erst
  // nachdem Bene den echten Wert (entweder "scrolly-public" oder
  // "scrolly-admin") gesetzt hat.
  // eslint-disable-next-line no-console
  console.warn(
    "[OIDC] VITE_KEYCLOAK_PUBLIC_CLIENT_ID ist noch Platzhalter — " +
      "Auth wird nicht funktionieren bis ein echter Wert gesetzt ist.",
  );
}

/**
 * OIDC-Config für public/-Stack (viewer.scrolly.com).
 *
 * Wird in OidcAuthProvider als props gespreaded.
 *
 * Wichtig: redirect_uri wird aus `window.location.origin` berechnet —
 * darum kann diese Config NUR client-side evaluiert werden. Vike-SSR
 * darf sie nicht durchlaufen, daher wrappt OidcAuthProvider sich in
 * `<ClientOnly>`.
 */
export function getOidcConfig(): AuthProviderProps {
  return {
    authority: KEYCLOAK_ISSUER,
    client_id: CLIENT_ID,
    redirect_uri: window.location.origin + "/oidc-callback",
    // Kein post_logout_redirect_uri — public/-Stack hat heute keinen
    // SignOut-Button. Logout via portal/.
    response_type: "code",
    scope: "openid profile email",
    automaticSilentRenew: true,
    loadUserInfo: false, // Pre-Verify Q-3.3-5: kein /auth/me-Call
    // PKCE (S256) ist Default in oidc-client-ts v3+ für public clients.
    // sessionStorage ist Default-Store in react-oidc-context v3 — kein
    // expliziter userStore-Override nötig.

    // Silent-SSO-Timeout: <10s default. Wenn Keycloak slow/down,
    // failed Silent-SSO graceful zu anonym (Pre-Verify Q-3.3-2).
    silentRequestTimeoutInSeconds: 10,

    onSigninCallback: () => {
      // URL nach Callback bereinigen: nur die OIDC-Parameter entfernen. Seit
      // dem unsichtbaren Sprung (2026-09-18) landet der Callback auf der
      // Scrolly-Adresse selbst, deren uebrige Parameter bleiben erhalten.
      window.history.replaceState(
        {},
        document.title,
        stripAuthParams(window.location.href),
      );
    },
  };
}
